import { ReactNode } from 'react';
import styled from 'styled-components';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/plus-2.svg';
import { ReactComponent as VIcon } from '@elementary/common-ui/assets/v-green.svg';
import {
  FlexColumn,
  Flex,
  Button,
  FlexCenter,
} from '@elementary/common-ui/components';
import Tooltip from '@mui/material/Tooltip';
import { Typography, CircularProgress } from '@mui/material';

const Wrapper = styled(Flex)`
  min-height: 112px;
  height: fit-content;
  width: 100%;
  background: ${({ theme }) => theme.palette.common.white};
  border: 1px solid ${({ theme }) => theme.palette.common.gray13};
  border-radius: 8px;
  align-items: center;
  justify-content: space-between;
`;

const DetailsSection = styled(Flex)`
  padding: 24px;
  gap: 24px;
  align-items: start;
  flex: 1;
`;

const ActionsSection = styled(Flex)`
  align-items: center;
  padding: 24px;
`;

const Spinner = styled(CircularProgress)`
  color: ${({ theme }) => theme.palette.common.slate};
`;

const ConnectedIndicator = () => (
  <Flex sx={{ gap: '6px', alignItems: 'center' }}>
    <VIcon width={18} />
    <Typography sx={{ color: 'success.main', fontWeight: 'bold' }}>
      Connected
    </Typography>
  </Flex>
);

type Props = {
  title: string;
  description: string;
  info?: string;
  addText?: string;
  icon?: ReactNode;
  connected?: boolean;
  loading?: boolean;
  onOpen: () => void;
  disabled?: boolean;
  connectTooltipTitle?: ReactNode;
  supportMultipleDestinations?: boolean;
  multipleDestinationsRenderer?: () => ReactNode;
  onMultipleDestinationsConnectClick?: () => void;
  connectedButtonText?: string;
  hideAddButton?: boolean;
};

const IntegrationBox = ({
  title,
  description,
  info,
  addText,
  icon,
  connected,
  loading,
  onOpen,
  disabled,
  connectTooltipTitle,
  supportMultipleDestinations,
  multipleDestinationsRenderer,
  onMultipleDestinationsConnectClick,
  connectedButtonText = 'Edit',
  hideAddButton = false,
}: Props) => (
  <Wrapper>
    <DetailsSection>
      {icon && (
        <FlexCenter sx={{ svg: { visibility: 'visible' } }}>{icon}</FlexCenter>
      )}
      <FlexColumn sx={{ gap: 1, flex: 1 }}>
        <Flex sx={{ gap: 2, height: '20px' }}>
          <Typography sx={{ fontWeight: 'bold', lineHeight: '20px' }}>
            {title}
          </Typography>
          {connected && !supportMultipleDestinations && <ConnectedIndicator />}
        </Flex>
        <Typography sx={{ lineHeight: '18px', color: 'common.gray2' }}>
          {description}
        </Typography>
        {info && (
          <Typography variant="caption" sx={{ color: 'common.gray2' }}>
            {info}
          </Typography>
        )}
        {supportMultipleDestinations && multipleDestinationsRenderer && (
          <FlexColumn sx={{ mt: 3, gap: '24px' }}>
            {multipleDestinationsRenderer()}
            {!hideAddButton && (
              <Tooltip
                title={connectTooltipTitle}
                placement="top-start"
                disableHoverListener={!connectTooltipTitle}
              >
                <span style={{ display: 'inline-flex', width: 'fit-content' }}>
                  <Button
                    sx={{ width: 'fit-content' }}
                    variant="text"
                    color="secondary"
                    onClick={onMultipleDestinationsConnectClick}
                    startIcon={<PlusIcon />}
                  >
                    {addText}
                  </Button>
                </span>
              </Tooltip>
            )}
          </FlexColumn>
        )}
      </FlexColumn>
    </DetailsSection>
    {!supportMultipleDestinations && (
      <ActionsSection sx={{ gap: 2 }}>
        {loading && (
          <Flex sx={{ gap: 1, alignItems: 'center' }}>
            <Spinner size={14} thickness={5} />
            <Typography
              noWrap
              sx={{ color: 'common.slate', fontWeight: 'bold' }}
            >
              Loading data
            </Typography>
          </Flex>
        )}
        {connected ? (
          <Button variant="outlined" onClick={() => onOpen()}>
            {connectedButtonText}
          </Button>
        ) : (
          <Tooltip
            title={connectTooltipTitle}
            placement="top-start"
            disableHoverListener={!connectTooltipTitle}
          >
            <span style={{ display: 'inline-flex' }}>
              <Button disabled={disabled} onClick={() => onOpen()}>
                Connect
              </Button>
            </span>
          </Tooltip>
        )}
      </ActionsSection>
    )}
  </Wrapper>
);

export default IntegrationBox;
